import { AgentThreadVo, AgentThreadVoStatus } from "@/api";
import { VidMuseEvent } from "@/common/events";
import { logger } from "@/common/logger";
import { ChatBoxFiles } from "@/component/ChatBoxFiles";
import { Confirm } from "@/component/Confirm";
import { GradientBorder } from "@/component/GradientBorder";
import { useConfirmBeforeSend } from "@/hooks/useConfirmBeforeSend";
import { useSendMessage } from "@/hooks/useSendMessage";
import { PlusOutlined } from "@ant-design/icons";
import { Button, Input, Tooltip, Upload } from "antd";
import classNames from "classnames";
import Link from "next/link";
import { useCallback } from "react";
import SimpleBar from "simplebar-react";
import StopIcon from "./assets/stop.svg";
import { StatusIndicator } from "./components/StatusIndicator";
import styles from "./index.module.css";

const { TextArea } = Input;

interface IProps {
  thread: AgentThreadVo;
}

export function SendMessageForm(props: IProps) {
  const { thread } = props;
  const {
    text,
    handleMessageTextChange,
    handleSendMessage,
    attachmentList,
    uploadProps,
    onRemove,
    isSending,
    stopTooltipText,
    stoppingTooltipText,
    isRequestStopping,
    handleStop,
    handleContinue,
  } = useSendMessage(thread);
  const threadStatus = thread.status;
  const disabled =
    threadStatus === AgentThreadVoStatus.Stopping ||
    threadStatus === AgentThreadVoStatus.Pending ||
    isRequestStopping ||
    !thread.owner;

  const isStopping =
    threadStatus === AgentThreadVoStatus.Stopping || isRequestStopping;

  const inputDisabled =
    disabled || threadStatus === AgentThreadVoStatus.Running;

  const hasAttachments = attachmentList.length > 0;

  const {
    confirmOpen,
    setConfirmOpen,
    handleKeyDown: handleTextAreaKeyDown,
    handleConfirmSend,
  } = useConfirmBeforeSend(hasAttachments, handleSendMessage);

  const onTextAreaKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (inputDisabled) {
        return;
      }

      handleTextAreaKeyDown(e);
    },
    [handleTextAreaKeyDown, inputDisabled]
  );

  if (!thread.owner) {
    return (
      <div className="relative flex flex-col items-center gap-5 pb-4">
        <div className={styles.gradientBg}></div>

        {/* Toast notification */}
        <div className={styles.toast}>
          <div className={styles.toastText}>
            👉 Don&apos;t just watch — bring your ideas to life!
          </div>
        </div>

        {/* New chat button */}
        <div className={styles.newChatButtonContainer}>
          <Link href="/">
            <Button
              type="primary"
              size="large"
              className="w-[140px] font-medium! z-10"
              onClick={() =>
                logger.event(VidMuseEvent.ThreadPageTryForFreeBtnClick, {
                  thread_id: thread.id || "",
                })
              }
            >
              Try For Free
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full relative">
      <div className={styles.gradientBg}></div>

      <StatusIndicator
        status={threadStatus}
        onContinue={handleContinue}
        isSending={isSending || text.length > 0}
      />

      <div className="relative z-10 rounded-2xl p-3 bg-background-container">
        <GradientBorder
          className="w-full h-full absolute top-0 left-0 z-2 pointer-events-none"
          borderRadius={16}
          borderWidth={1}
        />

        {attachmentList.length > 0 && (
          <div className="pb-3">
            <ChatBoxFiles
              attachmentList={attachmentList}
              onRemove={onRemove}
              size="small"
            />
          </div>
        )}
        <SimpleBar style={{ maxHeight: "452px" }}>
          <TextArea
            value={text}
            onChange={(e) => handleMessageTextChange(e.target.value)}
            onKeyDown={onTextAreaKeyDown}
            placeholder="Want to adjust something? Tell me here…"
            autoSize={{ minRows: 2 }}
            className={classNames(styles.textarea, "text-text-primary!")}
          />
        </SimpleBar>
        <div className={styles.footer}>
          <Upload
            className="w-9 h-9 flex items-center justify-center"
            {...uploadProps}
            disabled={inputDisabled}
          >
            <PlusOutlined
              className={classNames(styles.smallUploadButton, {
                [styles.disabled]: inputDisabled,
              })}
            />
          </Upload>
          <div className={styles.footerRight}>
            {threadStatus === AgentThreadVoStatus.Running || isStopping ? (
              <Tooltip
                placement="left"
                title={isStopping ? stoppingTooltipText : stopTooltipText}
              >
                <Button
                  loading={isStopping}
                  onClick={handleStop}
                  icon={<StopIcon />}
                />
              </Tooltip>
            ) : (
              <Confirm
                title="Using content you don't own may violate copyright laws. By continuing, you confirm this is your own original content."
                open={confirmOpen}
                onOpenChange={setConfirmOpen}
                onConfirm={handleConfirmSend}
                skipConfirm={!hasAttachments}
                disabled={isSending || disabled}
              >
                <Button
                  type="primary"
                  loading={isSending || disabled}
                  className="!h-[36px] !font-medium !px-[16px] disabled:!opacity-20 disabled:!bg-primary"
                  disabled={isSending || disabled || !text}
                >
                  Submit
                </Button>
              </Confirm>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
